import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import { SERVICES, FAQS } from "@/data/mock";

export default function Services() {
  return (
    <div data-testid="services-page" className="bg-brand-bg">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
        <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
          Services
        </p>
        <h1 className="mt-4 font-display text-5xl md:text-7xl font-semibold tracking-tighter leading-[0.95] text-brand-ink max-w-4xl text-balance">
          Five things we do, all of them properly.
        </h1>
        <p className="mt-6 text-lg text-brand-inkSoft max-w-2xl">
          We don’t pretend to be everything. We do the work we know inside out and we do it well —
          from a single brick wall to a full ground-up home.
        </p>
      </section>

      {SERVICES.map((s, i) => {
        const tints = [
          "bg-brand-bg",
          "bg-brand-bgOchreSoft border-y border-brand-line",
          "bg-brand-bgSageSoft border-y border-brand-line",
          "bg-brand-bgTerracottaSoft border-y border-brand-line"
        ];
        const cls = tints[i % tints.length];
        return (
        <section
          id={s.slug}
          key={s.slug}
          data-testid={`service-section-${s.slug}`}
          className={cls}
        >
          <div
            className={`max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center ${
              i % 2 === 1 ? "md:[&>*:first-child]:order-last" : ""
            }`}
          >
            <div className="md:col-span-6">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden border border-brand-line">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="md:col-span-6">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
                0{i + 1} · {s.slug}
              </p>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-medium tracking-tight text-brand-ink">
                {s.title}
              </h2>
              <p className="mt-5 text-brand-inkSoft leading-relaxed text-lg">{s.description}</p>
              <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                {s.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-2 text-brand-ink text-[15px]"
                  >
                    <span className="mt-1 inline-flex w-5 h-5 rounded-full bg-brand-terracotta/10 text-brand-terracotta items-center justify-center">
                      <Check className="w-3 h-3" />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Button
                  asChild
                  data-testid={`service-cta-${s.slug}`}
                  className="rounded-full h-12 px-7 bg-brand-terracotta hover:bg-brand-terracottaDark text-white"
                >
                  <Link to={`/contact?service=${s.slug}`}>
                    Get a {s.title.toLowerCase()} quote <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
        );
      })}

      {/* FAQ */}
      <section className="bg-brand-bgOchreSoft border-t border-brand-line">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
            Common questions
          </p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-medium tracking-tight text-brand-ink">
            The bits everyone asks.
          </h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="services-faq">
            {FAQS.map((f, i) => (
              <AccordionItem
                value={`item-${i}`}
                key={i}
                className="border-b border-brand-line"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="font-display text-lg md:text-xl tracking-tight text-brand-ink hover:text-brand-terracotta py-5"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-brand-inkSoft text-[15px] leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
